import { useEffect, useMemo, useRef, useState } from 'react'
import { IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useTheme } from '@mui/material'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'

const isSelectableRow = (row, mode) => !(mode === 'add' && row.AssignmentID)
const isTripActionDisabled = (row) => !row.VehicleNo || !(Number(row.TotalCapacity) > 0)

const headerCheckboxSx = {
  '& input[type="checkbox"]': {
    appearance: 'none',
    WebkitAppearance: 'none',
    width: 14,
    height: 14,
    margin: 0,
    border: '1px solid #fff',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    verticalAlign: 'middle',
  },
  '& input[type="checkbox"]:checked': {
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23fff' stroke-width='2' d='M3 8l3.5 3.5L13 5'/%3E%3C/svg%3E\")",
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
  },
  '& input[type="checkbox"]:indeterminate': {
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath stroke='%23fff' stroke-width='2' d='M3 8h10'/%3E%3C/svg%3E\")",
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
  },
  '& input[type="checkbox"]:disabled': {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
}

const SimpleTable = ({
  headers = [],
  data = [],

  selectedId,
  onSelect,
  onCheck,
  mode,
  onView,
  searchData = {},
  onSearchChange,

  showTotals = false,
  totals = {},
  totalLabel = 'TOTAL',

  minWidth = 1200,
}) => {
  const theme = useTheme()
  const showFilterRow = headers.some((column) => column.showfilter)
  const [headerCheck, setHeaderCheck] = useState(0)
  const headerCheckRef = useRef(null)

  const selectableRows = useMemo(
    () => (onCheck ? data.filter((row) => isSelectableRow(row, mode)) : []),
    [data, mode, onCheck]
  )

  const syncHeaderCheck = (rows) => {
    let count = 0
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].checked === true) count += 1
    }
    if (count === rows.length && rows.length > 0) {
      setHeaderCheck(1)
    } else if (count > 0 && count < rows.length) {
      setHeaderCheck(2)
    } else if (count === 0) {
      setHeaderCheck(0)
    }
  }

  useEffect(() => {
    if (!onCheck) return
    syncHeaderCheck(selectableRows)
  }, [selectableRows, onCheck])

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = headerCheck === 2
    }
  }, [headerCheck])

  const handleHeaderCheckboxChange = (e) => {
    setHeaderCheck(e.target.checked ? 1 : 0)
    onCheck(
      selectableRows.map((row) => row.BookingID),
      e.target.checked
    )
  }

  return (
    <TableContainer
      sx={{
        border: '1px solid #dce3ef',
        borderRadius: '6px',
        overflowX: 'auto',
      }}
    >
      <Table
        stickyHeader
        size="small"
        sx={{
          minWidth,
          tableLayout: 'fixed',
        }}
      >
        <TableHead>
          <TableRow>
            {headers.map((column) => (
              <TableCell
                key={column.field}
                align={column.align || 'left'}
                sx={{
                  width: column.weight,
                  background: theme.palette.primary.main,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  borderRight: '1px solid rgba(255,255,255,.2)',
                  ...(column.field === 'select' && onCheck ? headerCheckboxSx : {}),
                }}
              >
                {column.field === 'select' && onCheck ? (
                  <input
                    ref={headerCheckRef}
                    type="checkbox"
                    checked={headerCheck === 1}
                    disabled={!selectableRows.length}
                    onChange={handleHeaderCheckboxChange}
                    aria-label="Select all"
                  />
                ) : (
                  column.header
                )}
              </TableCell>
            ))}
          </TableRow>
          {showFilterRow ? (
            <TableRow sx={{ '& .MuiTableCell-root': { padding: '6px !important' } }}>
              {headers.map((column) =>
                column.showfilter ? (
                  <TableCell
                    key={`filter-${column.field}`}
                    sx={{
                      background: '#fff',
                      borderRight: '1px solid #e6ebf3',
                    }}
                  >
                    <div className="filter-input" style={{ fontSize: '12px', justifyContent: 'space-between' }}>
                      <input
                        className="field"
                        value={searchData[column.field] || ''}
                        name="cancelSearchInput"
                        style={{ width: '100%' }}
                        placeholder="Contains"
                        onChange={(e) => onSearchChange?.(e, column.field)}
                      />
                      <FilterAltIcon sx={{ paddingRight: '0px !important', color: '#aaa !important' }} />
                    </div>
                  </TableCell>
                ) : (
                  <TableCell
                    key={`filter-${column.field}`}
                    sx={{
                      background: '#fff',
                      borderRight: '1px solid #e6ebf3',
                    }}
                  />
                )
              )}
            </TableRow>
          ) : null}
        </TableHead>

        <TableBody>
          {data.map((row, index) => (
            <TableRow key={row.VehicleID || row.LRID || index} hover>
              {headers.map((column) => {
                if (column.field === 'select' && onSelect) {
                  return (
                    <TableCell key={column.field} align="center">
                      <input
                        type="radio"
                        checked={selectedId === (row.VehicleID || row.id)}
                        onChange={() => onSelect(row.VehicleID || row.id, row)}
                        disabled={isTripActionDisabled(row)}
                      />
                    </TableCell>
                  )
                }

                if (column.field === 'select' && onCheck) {

                  return (
                    <TableCell key={column.field} align="center">
                      <input type="checkbox" disabled={mode === 'add' && row.AssignmentID} checked={row.checked} onChange={() => onCheck(row.BookingID)} />
                    </TableCell>
                  )
                }

                if (column.field === 'ViewLoad') {
                  return (
                    <TableCell
                      key={column.field}
                      align="center"
                      sx={{
                        borderRight: '1px solid #e6ebf3',
                        borderBottom: '1px solid #e0e5ee',
                      }}
                    >
                      <IconButton
                        aria-label={`View load for ${row.vehicleNo}`}
                        size="small"
                        disabled={isTripActionDisabled(row)}
                        onClick={() => onView(row)}
                        sx={{ border: '1px solid #dde6f5', borderRadius: '4px', color: theme.palette.primary.main, height: 30, width: 40 }}
                      >
                        <LocalShippingOutlinedIcon sx={{ fontSize: 25 }} />
                      </IconButton>
                    </TableCell>
                  )
                }
                return (
                  <TableCell
                    key={column.field}
                    align={column.align || 'left'}
                    sx={{
                      color: column.red ? '#d32f2f' : '#111827',
                      fontSize: 13,
                      fontWeight: column.field === 'TripName' ? 600 : 500,
                    }}
                  >
                    {row[column.field]}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}

          {showTotals && (
            <TableRow
              sx={{
                background: '#eef6ff',
              }}
            >
              {(() => {
                let skip = 0

                return headers.map((column, index) => {
                  if (skip > 0) {
                    skip--
                    return null
                  }
                  const colSpan = totals.colSpan?.[column.field] || 1
                  skip = colSpan - 1
                  return (
                    <TableCell
                      key={column.field}
                      colSpan={colSpan}
                      align={'right'}
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      {index === 0 ? totalLabel : (totals[column.field] ?? '')}
                    </TableCell>
                  )
                })
              })()}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default SimpleTable
